import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Users, ArrowLeft, Settings, Trash2, UserPlus, Calendar, Swords } from "lucide-react";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";

export default function MyCircuit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [circuit, setCircuit] = useState(null);
  const [rosterFighters, setRosterFighters] = useState([]);
  const [tab, setTab] = useState("overview"); // overview, fightcards
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [createFightCardModal, setCreateFightCardModal] = useState(false);
  const [busy, setBusy] = useState(false);

  // Edit form
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editStatus, setEditStatus] = useState("");
  
  // Fight card form
  const [fcName, setFcName] = useState("");
  const [fcDate, setFcDate] = useState("");
  const [fcNumFights, setFcNumFights] = useState(5);
  const [fcMatchups, setFcMatchups] = useState([]); // [{fighter1_id, fighter2_id}]
  const [fightCards, setFightCards] = useState([]);

  useEffect(() => {
    loadCircuit();
  }, []);

  const loadCircuit = async () => {
    try {
      const { data } = await api.get("/arena/circuits/my-circuit");
      
      if (!data.circuit) {
        // No circuit, redirect to create
        navigate("/arena-circuit/create-circuit");
        return;
      }

      setCircuit(data.circuit);
      setRosterFighters(data.roster_fighters || []);
      
      // Load fight cards
      loadFightCards(data.circuit.id);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  
  const loadFightCards = async (circuitId) => {
    try {
      const { data } = await api.get(`/arena-circuit/circuits/${circuitId}/fight-cards`);
      setFightCards(data.fight_cards || []);
    } catch (e) {
      console.error("Failed to load fight cards:", e);
    }
  };
  
  const openCreateFightCardModal = () => {
    // Reset form
    setFcName("");
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 7); // 1 week from now
    setFcDate(minDate.toISOString().split('T')[0]);
    setFcNumFights(5);
    setFcMatchups([]);
    setCreateFightCardModal(true);
  };
  
  const createFightCard = async () => {
    if (!fcName.trim()) {
      toast.error("Fight card name is required");
      return;
    }
    
    if (fcMatchups.length !== fcNumFights) {
      toast.error(`Please select ${fcNumFights} matchups`);
      return;
    }
    
    // Check for incomplete matchups
    const incomplete = fcMatchups.some(m => !m.fighter1_id || !m.fighter2_id);
    if (incomplete) {
      toast.error("All matchups must have 2 fighters selected");
      return;
    }
    
    setBusy(true);
    try {
      const { data } = await api.post(`/arena-circuit/circuits/${circuit.id}/fight-cards`, {
        name: fcName.trim(),
        event_date: fcDate,
        matchups: fcMatchups
      });
      
      toast.success(data.message || "Fight card created! Offers sent to fighters.");
      setCreateFightCardModal(false);
      loadFightCards(circuit.id);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openEditModal = () => {
    setEditName(circuit.name);
    setEditDescription(circuit.description || "");
    setEditStatus(circuit.status);
    setEditModal(true);
  };

  const handleUpdate = async () => {
    if (editName.trim().length < 3) {
      toast.error("Circuit name must be at least 3 characters");
      return;
    }

    setBusy(true);
    try {
      await api.put(`/arena/circuits/${circuit.id}`, {
        name: editName.trim(),
        description: editDescription.trim(),
        status: editStatus
      });

      toast.success("Circuit updated successfully");
      setEditModal(false);
      await loadCircuit();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/arena/circuits/${circuit.id}`);
      toast.success(data.message);
      navigate("/arena-circuit");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  if (!circuit) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Trophy}
          title="My Circuit"
          subtitle="Manage your arena circuit"
        />
        <Empty label="No Circuit">
          <p className="text-sm text-zinc-400 mb-4">
            You haven&apos;t created a circuit yet
          </p>
          <button
            onClick={() => navigate("/arena-circuit/create-circuit")}
            className={btn()}
          >
            Create Circuit
          </button>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <PageHeader
            icon={Trophy}
            title={circuit.name}
            subtitle="Circuit Management Dashboard"
          />
        </div>
        <button
          onClick={openEditModal}
          className={btn() + " flex items-center gap-2"}
        >
          <Settings size={16} />
          Settings
        </button>
      </div>

      {/* Circuit Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="border border-amber-900/30 bg-zinc-950 p-4 text-center">
          <div className="text-2xl font-cinzel text-amber-400">
            {circuit.stats?.total_fighters || 0}
          </div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
            Gladiators
          </div>
        </div>
        <div className="border border-amber-900/30 bg-zinc-950 p-4 text-center">
          <div className="text-2xl font-cinzel text-amber-400">
            {circuit.stats?.total_bouts || 0}
          </div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
            Bouts
          </div>
        </div>
        <div className="border border-amber-900/30 bg-zinc-950 p-4 text-center">
          <div className="text-2xl font-cinzel text-amber-400">
            {circuit.stats?.fight_cards || 0}
          </div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
            Fight Cards
          </div>
        </div>
        <div className="border border-amber-900/30 bg-zinc-950 p-4 text-center">
          <div className={`inline-block px-3 py-1 text-xs rounded-sm ${
            circuit.status === "recruiting"
              ? "bg-emerald-900/30 border border-emerald-600/50 text-emerald-300"
              : circuit.status === "active"
              ? "bg-amber-900/30 border border-amber-600/50 text-amber-300"
              : "bg-zinc-800 text-zinc-400"
          }`}>
            {circuit.status === "recruiting" ? "RECRUITING" : circuit.status === "active" ? "NOT RECRUITING" : circuit.status.toUpperCase()}
          </div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
            Status
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800">
        <div className="flex gap-4">
          <button
            onClick={() => setTab("overview")}
            className={`px-4 py-3 text-sm font-cinzel uppercase tracking-wide border-b-2 transition-colors flex items-center gap-2 ${
              tab === "overview" ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Users size={16} />
            Roster
          </button>
          <button
            onClick={() => setTab("fightcards")}
            className={`px-4 py-3 text-sm font-cinzel uppercase tracking-wide border-b-2 transition-colors flex items-center gap-2 ${
              tab === "fightcards" ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Swords size={16} />
            Fight Cards
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {tab === "overview" && (
        <>
      {/* Roster */}
      <div className="border border-amber-900/30 bg-zinc-950 p-6">
        <h3 className="font-cinzel text-xl text-amber-300 mb-4 uppercase tracking-wide flex items-center gap-2">
          <Users size={20} />
          Circuit Roster
        </h3>
        {rosterFighters.length === 0 ? (
          <div className="text-center py-8">
            <Users size={48} className="text-amber-700/50 mx-auto mb-4" />
            <h4 className="font-cinzel text-lg text-zinc-300 mb-2">No Gladiators Yet</h4>
            <p className="text-sm text-zinc-500 mb-4">
              Start recruiting gladiators by offering contracts
            </p>
            <button
              onClick={() => navigate("/arena-circuit/recruit")}
              className={btn()}
            >
              <UserPlus size={16} className="inline mr-2" />
              Recruit Gladiators
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rosterFighters.map((fighter) => (
              <div
                key={fighter.id}
                className="border border-zinc-800 bg-zinc-900/50 p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-cinzel text-amber-300">{fighter.name}</h4>
                    <p className="text-xs text-zinc-500 capitalize">{fighter.cls}</p>
                  </div>
                  {fighter.corona_holder && (
                    <Trophy size={18} className="text-amber-400" />
                  )}
                </div>
                <div className="text-xs text-zinc-400">
                  {fighter.record.wins}W / {fighter.record.losses}L ({fighter.record.bouts} bouts)
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border border-red-900/30 bg-red-950/10 p-6">
        <h3 className="font-cinzel text-red-300 mb-3 uppercase tracking-wide">Danger Zone</h3>
        <p className="text-sm text-zinc-400 mb-4">
          Closing your circuit will release all fighters and permanently end operations
        </p>
        <button
          onClick={() => setDeleteModal(true)}
          className="px-4 py-2 bg-red-900/20 border border-red-700/40 text-red-300 hover:bg-red-900/30 transition-colors text-sm flex items-center gap-2"
        >
          <Trash2 size={14} />
          Close Circuit
        </button>
      </div>
      </>
      )}

      {/* Fight Cards Tab */}
      {tab === "fightcards" && (
        <div className="border border-amber-900/30 bg-zinc-950 p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="font-cinzel text-xl text-amber-300 uppercase tracking-wide">Fight Cards</h3>
              <p className="text-sm text-zinc-500 mt-1">Schedule fights between your circuit gladiators</p>
            </div>
            <button onClick={openCreateFightCardModal} className={btn() + " flex items-center gap-2"} disabled={rosterFighters.length < 2}>
              <Calendar size={16} />
              Create Fight Card
            </button>
          </div>
          
          {fightCards.length === 0 ? (
            <Empty icon={<Swords size={48} />} title="No Fight Cards" desc="Create your first fight card to schedule matchups between your circuit gladiators." />
          ) : (
            <div className="space-y-4">
              {fightCards.map((card) => (
                <div key={card.id} className="border border-zinc-800 bg-zinc-900/50 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-cinzel text-lg text-amber-300">{card.name}</h4>
                      <p className="text-xs text-zinc-500 mt-1">
                        Event Date: {new Date(card.event_date).toLocaleDateString()} · {card.matchups?.length || 0} Matchups
                      </p>
                    </div>
                    <span className={`px-3 py-1 text-xs uppercase tracking-wide rounded-sm ${
                      card.status === "pending" ? "bg-amber-900/30 border border-amber-600/50 text-amber-300" :
                      card.status === "confirmed" ? "bg-emerald-900/30 border border-emerald-600/50 text-emerald-300" :
                      "bg-red-900/30 border border-red-600/50 text-red-300"
                    }`}>
                      {card.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {(card.matchups || []).map((matchup, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-zinc-950/50 border border-zinc-800/50 px-3 py-2">
                        <span className="text-zinc-300">{matchup.fighter1_name}</span>
                        <span className="text-amber-500 font-semibold mx-3">VS</span>
                        <span className="text-zinc-300">{matchup.fighter2_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <Modal open={editModal} onClose={() => setEditModal(false)} title="Edit Circuit">
          <div className="space-y-4">
            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">
                Circuit Name
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={50}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">
                Description
              </label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                maxLength={500}
                rows={4}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none resize-none"
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">
                Status
              </label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              >
                <option value="recruiting">Recruiting (Default)</option>
                <option value="active">Not Recruiting</option>
              </select>
              <p className="text-xs text-zinc-500 mt-2">
                Recruiting: Open to new gladiators (Green) · Not Recruiting: Closed roster (Amber)
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setEditModal(false)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className={btn() + " flex-1"}
                disabled={busy}
              >
                {busy ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <Modal open={deleteModal} onClose={() => setDeleteModal(false)} title="Close Circuit">
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Are you sure you want to close <span className="text-amber-300 font-cinzel font-semibold">{circuit.name}</span>?
            </p>
            <div className="bg-red-950/20 border border-red-700/40 rounded p-3">
              <p className="text-xs text-red-300">
                ⚠️ This action cannot be undone. All contracts will be terminated and fighters will be released.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal(false)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className={btn() + " flex-1 bg-red-600 hover:bg-red-500"}
                disabled={busy}
              >
                {busy ? "Closing..." : "Close Circuit"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Create Fight Card Modal */}
      {createFightCardModal && (
        <Modal open={createFightCardModal} onClose={() => setCreateFightCardModal(false)} title="Create Fight Card" wide={true}>
          <div className="space-y-4">
            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">Fight Card Name</label>
              <input
                type="text"
                value={fcName}
                onChange={(e) => setFcName(e.target.value)}
                placeholder="e.g., Summer Showdown"
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>
            
            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">Event Date (min 1 week away)</label>
              <input
                type="date"
                value={fcDate}
                onChange={(e) => setFcDate(e.target.value)}
                min={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>
            
            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">Number of Fights (3-10)</label>
              <input
                type="number"
                value={fcNumFights}
                onChange={(e) => {
                  const num = Math.min(10, Math.max(3, parseInt(e.target.value) || 3));
                  setFcNumFights(num);
                  // Adjust matchups array to match new size
                  setFcMatchups(prev => {
                    const newArr = [...prev];
                    if (newArr.length < num) {
                      // Add empty matchups
                      while (newArr.length < num) {
                        newArr.push({ fighter1_id: "", fighter2_id: "" });
                      }
                    } else if (newArr.length > num) {
                      // Remove excess matchups
                      return newArr.slice(0, num);
                    }
                    return newArr;
                  });
                }}
                min="3"
                max="10"
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>
            
            {/* Matchups Selection */}
            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">Select Matchups</label>
              {rosterFighters.length < 2 ? (
                <div className="bg-red-950/20 border border-red-700/40 rounded p-3">
                  <p className="text-xs text-red-300">
                    ⚠️ You need at least 2 fighters in your roster to create matchups. Recruit more gladiators first.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {Array.from({ length: fcNumFights }).map((_, idx) => {
                    const matchup = fcMatchups[idx] || { fighter1_id: "", fighter2_id: "" };
                    
                    // Get fighters already selected in other matchups
                    const usedFighters = fcMatchups.reduce((acc, m, i) => {
                      if (i !== idx) {
                        if (m.fighter1_id) acc.add(m.fighter1_id);
                        if (m.fighter2_id) acc.add(m.fighter2_id);
                      }
                      return acc;
                    }, new Set());
                    
                    return (
                      <div key={idx} className="border border-zinc-800 bg-zinc-900/50 p-3">
                        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Matchup {idx + 1}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <select
                            value={matchup.fighter1_id}
                            onChange={(e) => {
                              const newMatchups = [...fcMatchups];
                              if (!newMatchups[idx]) newMatchups[idx] = { fighter1_id: "", fighter2_id: "" };
                              newMatchups[idx].fighter1_id = e.target.value;
                              setFcMatchups(newMatchups);
                            }}
                            className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-amber-600 outline-none"
                          >
                            <option value="">Select Fighter 1</option>
                            {rosterFighters
                              .filter(f => !usedFighters.has(f.id) || f.id === matchup.fighter1_id)
                              .map(fighter => (
                                <option key={fighter.id} value={fighter.id}>
                                  {fighter.name} ({fighter.cls}) - {fighter.record.wins}W/{fighter.record.losses}L
                                </option>
                              ))}
                          </select>
                          
                          <select
                            value={matchup.fighter2_id}
                            onChange={(e) => {
                              const newMatchups = [...fcMatchups];
                              if (!newMatchups[idx]) newMatchups[idx] = { fighter1_id: "", fighter2_id: "" };
                              newMatchups[idx].fighter2_id = e.target.value;
                              setFcMatchups(newMatchups);
                            }}
                            className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-amber-600 outline-none"
                          >
                            <option value="">Select Fighter 2</option>
                            {rosterFighters
                              .filter(f => !usedFighters.has(f.id) || f.id === matchup.fighter2_id)
                              .filter(f => f.id !== matchup.fighter1_id)
                              .map(fighter => (
                                <option key={fighter.id} value={fighter.id}>
                                  {fighter.name} ({fighter.cls}) - {fighter.record.wins}W/{fighter.record.losses}L
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <button onClick={() => setCreateFightCardModal(false)} className={btn() + " flex-1 bg-zinc-800"} disabled={busy}>
                Cancel
              </button>
              <button onClick={createFightCard} className={btn() + " flex-1"} disabled={busy || rosterFighters.length < 2}>
                {busy ? "Creating..." : "Create Fight Card"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
